import { useMemo, type ReactNode } from 'react';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { canViewField } from '@wlb/shared/utils/profileVisibility';
import type { ViewerContext } from '@wlb/shared/utils/profileVisibility';
import type { Profile } from '@wlb/shared/types';
import { Avatar } from '../../components/ui/Avatar';
import { CameraIcon } from '../../components/ui/icons';
import { getProfileAvatarUrl } from './useCurrentProfile';
import { buildProfileChips } from './profileChips';
import { ProfileInfoChips } from './ProfileInfoChips';
import { ProfileStatsBar, type ProfileConnectionStatKey } from './ProfileStatsBar';
import './profile.css';

interface ProfileInfoCardProps {
  profile: Profile;
  viewer: ViewerContext;
  /** Owner only: opens the profile-photo picker. */
  onChangePhoto?: () => void;
  onSelectStat?: (key: ProfileConnectionStatKey) => void;
  /** The two call-to-action buttons under the chips. */
  actions?: ReactNode;
}

export function ProfileInfoCard({
  profile,
  viewer,
  onChangePhoto,
  onSelectStat,
  actions,
}: ProfileInfoCardProps) {
  const { t } = useTranslation();
  const chips = useMemo(() => buildProfileChips(profile, viewer), [profile, viewer]);
  const showBio = Boolean(profile.bio) && canViewField(profile.bioPrivacy, viewer);
  const avatarUrl = canViewField(profile.profilePicturePrivacy, viewer)
    ? getProfileAvatarUrl(profile)
    : '';

  return (
    <section className="profile-card">
      <div className="profile-card__identity">
        <div className="profile-card__avatar">
          <Avatar
            src={avatarUrl}
            name={profile.name}
            username={profile.userName}
            seed={profile.userGuid ?? profile.userId}
            size="xl"
            square
          />
          {onChangePhoto && (
            <button
              type="button"
              className="profile-card__avatar-badge"
              aria-label={t('profile.accessibility.changePhoto')}
              onClick={onChangePhoto}
            >
              <CameraIcon size={14} />
            </button>
          )}
        </div>
        <div className="profile-card__identity-text">
          <h1 className="profile-card__name">{profile.name}</h1>
          {showBio && <p className="profile-card__bio">{profile.bio}</p>}
        </div>
      </div>

      <ProfileStatsBar profile={profile} onSelect={onSelectStat} />
      <ProfileInfoChips chips={chips} />

      {actions && <div className="profile-card__actions">{actions}</div>}
    </section>
  );
}
